import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, observable, reaction} from 'mobx';
import {inject, observer} from 'mobx-react';
import autobind from 'autobind-decorator';

import {withI18n} from 'spa/Components/Context';

import Button, {
  ButtonSizeTypes,
  ButtonThemeTypes,
  ButtonType,
} from '@urbanmassage/component-library/lib/Button/Button';
import Cancel from '@urbanmassage/component-library/lib/Icons/Cancel/Cancel';
import Divider from '@urbanmassage/component-library/lib/Divider/Divider';
import Fieldset from '@urbanmassage/component-library/lib/Fieldset/Fieldset';
import Radio from '@urbanmassage/component-library/lib/Radio/Radio';
import Typography, {TypeVariants} from '@urbanmassage/component-library/lib/Typography/Typography';

import ReviewsFiltersSidebarContentWrapper from './ReviewsFiltersSidebarContentWrapper';
import ReviewsFiltersSidebarLoadingState from './ReviewsFiltersSidebarLoadingState';

import {cn} from 'spa/css';

import 'spa/Components/Reviews/reviews-filters-sidebar.less';

import {CRITICAL_SORT_VALUE, RECENT_SORT_VALUE, POSITIVE_SORT_VALUE} from 'spa/Components/Reviews/values';

@inject(({
  store: {
    tracker,
  },
  therapistsState,
}) => ({
  therapistsState,
  tracker,
}))
@observer
@withI18n
class ReviewsFiltersSidebar extends React.Component {
  static propTypes = {
    isAnimatingPanel: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    therapistsState: PropTypes.shape({
      filtersApplied: PropTypes.bool,
      offset: PropTypes.number.isRequired,
      selectedId: PropTypes.string.isRequired,
      sortReviews: PropTypes.string.isRequired,
      specialitiesFilter: PropTypes.number,
      workerReviews: PropTypes.shape({
        getSearch: PropTypes.func.isRequired,
        maybeLoadSearch: PropTypes.func.isRequired,
        searches: PropTypes.shape({
          clear: PropTypes.func,
        }).isRequired,
      }).isRequired,
      workerSpecialities: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string,
          id: PropTypes.number,
        })),
        PropTypes.object,
      ]).isRequired,
    }).isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
  }

  static defaultProps = {
    isAnimatingPanel: false,
  }

  constructor(props, context) {
    super(props);

    const {i18n} = context;

    this.sortBy = this.props.therapistsState.sortReviews || RECENT_SORT_VALUE;

    this.sortOptions = [
      {
        value: RECENT_SORT_VALUE,
        label: i18n._x('Most recent', 'Review filter sort option'),
      },
      {
        value: POSITIVE_SORT_VALUE,
        label: i18n._x('Most positive', 'Review filter sort option'),
      },
      {
        value: CRITICAL_SORT_VALUE,
        label: i18n._x('Most critical', 'Review filter sort option'),
      },
    ];
  }

  componentDidMount() {
    this.listeners = [
      reaction(
        () => [
          this.props.therapistsState.specialitiesFilter,
          this.props.therapistsState.sortReviews,
        ],
        ([specialitiesFilter, sortReviews]) => {
          this.filterTreatment = specialitiesFilter;
          this.sortBy = sortReviews;
        },
        {
          fireImmediately: true,
          name: 'Update internal filterTreatment and sortBy on external change',
        },
      ),
    ];
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  @observable sortBy;
  @observable filterTreatment;

  @autobind
  @action('onClickApplyReviewFilters - ReviewsFiltersSidebar')
  onClickApplyReviewFilters() {
    const {
      onClose,
      therapistsState,
      therapistsState: {
        selectedId,
      },
      tracker,
    } = this.props;

    therapistsState.workerReviews.searches.clear();

    therapistsState.specialitiesFilter = this.filterTreatment;
    therapistsState.sortReviews = this.sortBy;
    therapistsState.offset = 0;
    therapistsState.filtersApplied = true;

    if (this.filterTreatment) {
      tracker.track('Selected a value to filter reviews', {
        category: 'Workers',
        label: 'Reviews',
        worker: {
          id: selectedId,
        },
        data: {
          speciality: this.filterTreatment,
        },
      });
    }

    tracker.track('Selected a value to sort reviews', {
      category: 'Workers',
      label: 'Reviews',
      worker: {
        id: selectedId,
      },
      data: {
        value: this.sortBy,
      },
    });

    if (onClose) {
      onClose();
    }
  }

  @computed get specialitiesOptions() {
    const {therapistsState: {workerSpecialities}} = this.props;

    if (workerSpecialities && workerSpecialities.length) {
      return workerSpecialities.map(speciality => ({
        value: speciality.id,
        label: speciality.name,
      }));
    }

    return [];
  }

  @autobind
  @action('onSelectSortReviewsBy - ReviewsFiltersSidebar')
  onSelectSortReviewsBy(event) {
    this.sortBy = event.target.value;
  }

  @autobind
  @action('onSelectFilterReviewsByTreatment - ReviewsFiltersSidebar')
  onSelectFilterReviewsByTreatment(event) {
    this.filterTreatment = parseInt(event.target.value, 10);
  }

  render() {
    const {i18n} = this.context;
    const {
      isAnimatingPanel,
      onClose,
    } = this.props;
    const {
      filterTreatment,
      onSelectFilterReviewsByTreatment,
      specialitiesOptions,
      onClickApplyReviewFilters,
      onSelectSortReviewsBy,
      sortBy,
      sortOptions,
    } = this;

    return (
      <ReviewsFiltersSidebarContentWrapper isAnimatingPanel={isAnimatingPanel}>
        {isAnimatingPanel ? (
          <ReviewsFiltersSidebarLoadingState
            isAnimatingPanel={isAnimatingPanel}
            onClose={onClose}
          />
        ) : (
          <React.Fragment>
            <div className="reviews-filters-sidebar__close-button-container">
              <Button
                onClick={onClose}
                size={ButtonSizeTypes.medium}
                theme={ButtonThemeTypes.grey}
                type={ButtonType.button}
              >
                <Cancel />
              </Button>
            </div>
            <Typography
              className="reviews-filters-sidebar__title"
              component={TypeVariants.h2}
              margin="0 0 3rem"
              variant={TypeVariants.h1}
            >
              {i18n._x('Filter reviews', 'Therapist reviews filter sidebar')}
            </Typography>
            <div className="reviews-filters-sidebar__panel">
              <Fieldset
                legend={i18n._x('Sort by', 'Review filters category title')}
                omitLastDivider
                theme="sidebar"
              >
                {sortOptions && sortOptions.map(option => (
                  <Radio
                    checked={sortBy === option.label.toLowerCase() || sortBy === option.value}
                    key={`reviews-filter-sort-option-${option.value}`}
                    label={option.label}
                    name="reviews-filters-sort-options"
                    onChange={onSelectSortReviewsBy}
                    pre={false}
                    value={option.value}
                  />
                ))}
              </Fieldset>
            </div>
            <div className="reviews-filters-sidebar__divider-container">
              <Divider marginBottom="2.4" marginTop="0" size="12" />
            </div>
            <div className="reviews-filters-sidebar__panel">
              <Fieldset
                legend={i18n._x('Treatments', 'Review filters category title')}
                omitLastDivider
                theme="sidebar"
              >
                {specialitiesOptions && specialitiesOptions.map(option => (
                  <Radio
                    checked={filterTreatment === parseInt(option.value, 10)}
                    key={`reviews-filter-treatment-option-${option.value}`}
                    label={option.label}
                    name="reviews-filters-treatments-options"
                    onChange={onSelectFilterReviewsByTreatment}
                    pre={false}
                    value={option.value}
                  />
                ))}
              </Fieldset>
            </div>
            <div className="reviews-filters-sidebar__fixed-control">
              <Button
                block
                onClick={onClickApplyReviewFilters}
                size={ButtonSizeTypes.default}
                type={ButtonType.button}
              >
                {i18n._x('Apply Filters', 'Reviews Filters button')}
              </Button>
            </div>
          </React.Fragment>
        )}
      </ReviewsFiltersSidebarContentWrapper>
    );
  }
}

export default ReviewsFiltersSidebar;



// WEBPACK FOOTER //
// ./spa/Components/Reviews/ReviewsFiltersSidebar.jsx